import { useEffect, useState, useRef } from 'preact/hooks'
import { RefreshCcw, AlertCircle, AlertTriangle, Settings, Plus, Lock, Webhook } from 'lucide-preact'
import type { ComponentType } from 'preact'
import { integrationsApi } from '@/api/integrations'
import { IntegrationProvider, INTEGRATION_STATUS_COLORS, type IntegrationConnectionViewDto } from '@/types/integrations'
import { IntegrationPanel } from './components/IntegrationPanel'
import { HubSpotSetupWizard } from './hubspot/HubSpotSetupWizard'
import { SalesforceConnectPanel } from './salesforce/SalesforceConnectPanel'
import { DynamicsConnectPanel } from './dynamics/DynamicsConnectPanel'
import { crmBlockedReason, findConnectedCrm } from './crmSlot'

interface IntegrationsListProps { clientCompanyId: string }
interface ProviderMeta { id: IntegrationProvider; title: string; desc: string; logo?: string; Icon?: ComponentType<Record<string, unknown>> }

// Subcomponents ------------------------------------------------------------

// Shared shell for both card types. The Connected and Available grids are separate, so nothing
// aligns them automatically — min-h-44 floors every card at the height of a footer-bearing
// connection card, and cards still grow together (via grid row stretch) when content needs it.
const CARD_BASE = 'relative rounded-xl border p-5 min-h-44 flex flex-col text-left shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all duration-200'
const CARD_CONNECTABLE = 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:border-primary-300 dark:hover:border-primary-600 hover:shadow-lg cursor-pointer'
const CARD_BLOCKED = 'bg-slate-50 dark:bg-slate-800/40 border-slate-200 dark:border-slate-700/60 cursor-not-allowed'
const CARD_SHELL = `${CARD_BASE} ${CARD_CONNECTABLE}`

interface ProviderCardProps { provider: ProviderMeta; selected: boolean; blockedReason?: string | null; onSelect: () => void }
// A blocked card stays visible and stays focusable: the reason is the only thing it is there to
// say, and a `disabled` button would put that out of reach of the keyboard.
const ProviderCard = ({ provider, selected, blockedReason, onSelect }: ProviderCardProps) => {
    const isBlocked = !!blockedReason
    return (
        <button
            key={provider.id}
            onClick={isBlocked ? undefined : onSelect}
            type="button"
            aria-disabled={isBlocked || undefined}
            className={`${CARD_BASE} ${isBlocked ? CARD_BLOCKED : CARD_CONNECTABLE} ${selected && !isBlocked ? 'ring-2 ring-primary-500' : ''}`}
        >
            <div className="flex items-start justify-between gap-4">
                <div className={`w-14 h-14 flex items-center justify-center bg-slate-50 dark:bg-slate-700 rounded-lg overflow-hidden text-slate-700 dark:text-slate-200 ${isBlocked ? 'opacity-60 grayscale' : ''}`}>
                    {provider.Icon
                        ? <provider.Icon className="w-8 h-8" />
                        : <img src={provider.logo} alt={provider.title} className="max-h-12 max-w-12 object-contain" loading="lazy" />}
                </div>
                {isBlocked ? (
                    <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-1 rounded-md bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300">
                        <Lock className="w-3 h-3" /> Unavailable
                    </span>
                ) : (
                    <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-1 rounded-md bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
                        <Plus className="w-3 h-3" /> Connect
                    </span>
                )}
            </div>
            <p className={`mt-3 font-medium leading-tight line-clamp-1 ${isBlocked ? 'text-slate-500 dark:text-slate-400' : 'text-slate-900 dark:text-white'}`}>{provider.title}</p>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400 line-clamp-3 flex-1">{blockedReason ?? provider.desc}</p>
        </button>
    )
}

interface ConnectionCardProps {
    connection: IntegrationConnectionViewDto
    highlight: boolean
    providerMeta?: ProviderMeta
    onManage: () => void
}
const ConnectionCard = ({ connection: c, highlight, providerMeta, onManage }: ConnectionCardProps) => {
    const isError = c.Status === 'Error'
    const isWarning = !isError && c.IsSoftFailure === true
    const badgeColor = c.IsArchived
        ? 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200'
        : isWarning
            ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
            : INTEGRATION_STATUS_COLORS[c.Status || 'Active'] || 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200'
    const badgeLabel = c.IsArchived ? 'Archived' : isWarning ? 'Warning' : (c.Status || 'Active')

    return (
        <div
            key={c.Id || c.Name}
            onClick={onManage}
            role="button"
            tabIndex={0}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onManage() } }}
            className={`group ${CARD_SHELL} ${highlight ? 'ring-2 ring-primary-500' : ''} ${c.IsArchived ? 'opacity-60 grayscale' : ''}`}
        >
            <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-3">
                    <div className="w-14 h-14 flex items-center justify-center bg-slate-50 dark:bg-slate-700 rounded-lg overflow-hidden text-slate-700 dark:text-slate-200">
                        {providerMeta?.Icon
                            ? <providerMeta.Icon className="w-8 h-8" />
                            : providerMeta && <img src={providerMeta.logo} alt={providerMeta.title} className="max-h-12 max-w-12 object-contain" loading="lazy" />}
                    </div>
                    <div>
                        <p className="font-medium text-slate-900 dark:text-white leading-tight line-clamp-1">{c.Name || providerMeta?.title || '(Unnamed)'}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-1">{providerMeta?.title}</p>
                    </div>
                </div>
                <span className={`text-[10px] px-2 py-1 rounded-md font-medium self-start ${badgeColor}`}>{badgeLabel}</span>
            </div>

            {isWarning && (
                <p className="mt-2 flex items-center gap-1 text-xs text-amber-700 dark:text-amber-300">
                    <AlertTriangle className="w-3 h-3 shrink-0" />
                    Warning: {c.ConsecutiveFailureCount ?? 0} failures
                </p>
            )}
            {isError && (
                <p className="mt-2 flex items-center gap-1 text-xs text-red-700 dark:text-red-300">
                    <AlertCircle className="w-3 h-3 shrink-0" />
                    Integration disabled — review errors below
                </p>
            )}

            {/* Always rendered, even when empty: a description-less card would otherwise collapse
                44px shorter and only match its neighbours when the grid row happens to stretch it. */}
            <p className="mt-3 mb-4 min-h-4 text-xs text-slate-500 dark:text-slate-400 line-clamp-3 flex-1">{c.Description}</p>
            <div className="mt-auto pt-3 border-t border-slate-100 dark:border-slate-700/60 flex items-center justify-between">
                <button
                    type="button"
                    onClick={e => { e.stopPropagation(); onManage() }}
                    className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 dark:text-primary-400 hover:underline"
                >
                    <Settings className="w-3 h-3" /> Manage
                </button>
                <span className="text-[10px] text-slate-400 dark:text-slate-500">Updated {c.UpdatedUtc ? new Date(c.UpdatedUtc).toLocaleDateString() : '—'}</span>
            </div>
        </div>
    )
}

type ExtendedConnection = IntegrationConnectionViewDto

function useIntegrationsList(clientCompanyId: string) {
    const [connections, setConnections] = useState<ExtendedConnection[] | null>(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [selectedProvider, setSelectedProvider] = useState<IntegrationProvider | null>(null)
    const [manageConnectionId, setManageConnectionId] = useState<string | null>(null)
    const [justCreatedId, setJustCreatedId] = useState<string | null>(null)
    const loadCancelledRef = useRef(false)

    const load = async () => {
        if (!clientCompanyId) return
        loadCancelledRef.current = false
        setLoading(true)
        setError(null)
        const res = await integrationsApi.getIntegrationConnections(clientCompanyId)
        if (loadCancelledRef.current) return
        if (res.error) setError(res.error)
        if (res.data) setConnections(res.data)
        setLoading(false)
    }

    useEffect(() => {
        let cancelled = false
        const loadData = async () => {
            if (!clientCompanyId) return
            setLoading(true)
            setError(null)
            const res = await integrationsApi.getIntegrationConnections(clientCompanyId)
            if (cancelled) return
            if (res.error) setError(res.error)
            if (res.data) setConnections(res.data)
            setLoading(false)
        }
        loadData()
        return () => {
            cancelled = true
            loadCancelledRef.current = true
        }
    }, [clientCompanyId])

    const handleClosePanel = () => {
        setSelectedProvider(null)
        setManageConnectionId(null)
        setJustCreatedId(null)
    }

    const handleConnectionCreated = (connection: IntegrationConnectionViewDto) => {
        setConnections(prev => prev ? [connection, ...prev] : [connection])
        setJustCreatedId(connection.Id || null)
        setSelectedProvider(null)
    }

    const handleConnectionUpdated = (connectionId: string, updates: Partial<ExtendedConnection>) => {
        setConnections(prev => prev?.map(c => c.Id === connectionId ? { ...c, ...updates } : c) || null)
    }

    const handleConnectionDeleted = (connectionId: string) => {
        setConnections(prev => prev?.filter(c => c.Id !== connectionId) || null)
        setManageConnectionId(null)
    }

    const handleConnectionArchived = (connectionId: string, isArchived: boolean) => {
        setConnections(prev => prev?.map(c => c.Id === connectionId ? {
            ...c,
            UpdatedUtc: new Date().toISOString(),
            IsArchived: isArchived
        } : c) || null)
    }

    return {
        connections, loading, error,
        selectedProvider, setSelectedProvider,
        manageConnectionId, setManageConnectionId,
        justCreatedId,
        load,
        handleClosePanel,
        handleConnectionCreated,
        handleConnectionUpdated,
        handleConnectionDeleted,
        handleConnectionArchived,
    }
}

export function IntegrationsList({ clientCompanyId }: IntegrationsListProps) {
    const {
        connections, loading, error,
        selectedProvider, setSelectedProvider,
        manageConnectionId, setManageConnectionId,
        justCreatedId,
        load,
        handleClosePanel,
        handleConnectionCreated,
        handleConnectionUpdated,
        handleConnectionDeleted,
        handleConnectionArchived,
    } = useIntegrationsList(clientCompanyId)

    // Whether any connections exist
    const hasConnections = !!(connections && connections.length > 0)
    // Track existing provider ids (case-insensitive to tolerate backend/provider casing mismatches)
    const existingProviders = new Set((connections || []).map(c => (c.Provider || '').toString().toLowerCase()))

    // Provider catalog (logos served from remote file storage if VITE_ASSETS_BASE_URL defined)
    const assetBase = (import.meta as ImportMeta).env?.VITE_ASSETS_BASE_URL?.replace(/\/$/, '') || ''
    const logoUrl = (filename: string) => `${assetBase}/assets/integrations/${filename}`
    const allProviders: ProviderMeta[] = [
        {
            id: IntegrationProvider.Salesforce,
            title: 'Salesforce',
            desc: 'Sync accounts, contacts & campaign performance from your CRM.',
            logo: logoUrl('salesforce-logo.svg')
        },
        {
            id: IntegrationProvider.HubSpot,
            title: 'HubSpot',
            desc: 'Bring in lifecycle stages, deals & marketing engagement signals.',
            logo: logoUrl('hubspot-logo.svg')
        },
        {
            id: IntegrationProvider.Dynamics,
            title: 'Dynamics 365',
            desc: 'Sync accounts, contacts & opportunities from Microsoft Dynamics 365 (Dataverse).',
            logo: logoUrl('dynamics-logo.svg')
        },
        {
            id: IntegrationProvider.BigMarker,
            title: 'BigMarker',
            desc: 'Ingest webinar registrations & attendance to enrich engagement scoring.',
            logo: logoUrl('bigmarker-icon.png')
        },
        {
            id: IntegrationProvider.Webhook,
            title: 'Webhook',
            desc: 'Accept form submissions from external systems (Zapier, legacy CRMs, custom scripts) via authenticated HTTP POST.',
            Icon: Webhook
        }
    ]

    const activeProviderMeta = allProviders.find(p => p.id === selectedProvider) || null
    const connectedCrm = findConnectedCrm(connections)
    // Blocked cards sink below the connectable ones so "what can I add" stays the first thing read.
    const availableProviders = allProviders
        .filter(p => !existingProviders.has(p.id.toLowerCase()))
        .map(p => ({ meta: p, blockedReason: crmBlockedReason(connectedCrm, p.id) }))
        .sort((a, b) => Number(!!a.blockedReason) - Number(!!b.blockedReason))
    const managingConnection = manageConnectionId ? (connections || []).find(c => c.Id === manageConnectionId) : null

    // HubSpot uses its own wizard instead of the generic panel
    const managingProviderLower = managingConnection?.Provider?.toLowerCase()
    const isHubSpotSelected = selectedProvider === IntegrationProvider.HubSpot
    const isHubSpotManage = managingProviderLower === IntegrationProvider.HubSpot.toLowerCase()
    const hubSpotWizardOpen = isHubSpotSelected || isHubSpotManage
    // Salesforce is OAuth-based like HubSpot — it gets its own connect panel rather than the
    // generic API-key IntegrationPanel.
    const isSalesforceSelected = selectedProvider === IntegrationProvider.Salesforce
    const isSalesforceManage = managingProviderLower === IntegrationProvider.Salesforce.toLowerCase()
    const salesforceConnectOpen = isSalesforceSelected || isSalesforceManage
    // Dynamics 365 is OAuth-based like Salesforce — it gets its own connect panel too.
    const isDynamicsSelected = selectedProvider === IntegrationProvider.Dynamics
    const isDynamicsManage = managingProviderLower === IntegrationProvider.Dynamics.toLowerCase()
    const dynamicsConnectOpen = isDynamicsSelected || isDynamicsManage
    const panelOpen = !!(activeProviderMeta || managingConnection) && !hubSpotWizardOpen && !salesforceConnectOpen && !dynamicsConnectOpen

    return (
        <div className="px-6 py-4 max-w-7xl mx-auto space-y-10">
            {/* Header */}
            <div className="flex items-start justify-between flex-wrap gap-4">
                <div>
                    <h2 className="text-xl font-semibold text-slate-900 dark:text-white">Integrations</h2>
                    <p className="text-sm mt-1 text-slate-600 dark:text-slate-400 max-w-prose">Connect external platforms to enrich data and automate workflows.</p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={load}
                        className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 transition-colors"
                        disabled={loading}
                    >
                        <RefreshCcw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> {loading ? 'Refreshing' : 'Refresh'}
                    </button>
                </div>
            </div>

            {/* Connected Section */}
            <section className="space-y-4">
                <div className="flex items-center gap-3">
                    <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Connected</h3>
                    {hasConnections && <span className="text-xs text-slate-400 dark:text-slate-500">{connections!.length} active</span>}
                </div>
                {!loading && !hasConnections && !error && (
                    <div className="p-6 border border-dashed rounded-lg border-slate-300 dark:border-slate-600 text-center text-sm text-slate-500 dark:text-slate-400">No connections yet. Connect a provider below.</div>
                )}
                {error && (
                    <div className="flex items-start gap-2 p-4 rounded-lg bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">
                        <AlertCircle className="w-4 h-4 mt-0.5" />
                        <span>{error}</span>
                    </div>
                )}
                {loading && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {Array.from({ length: 3 }).map((_, i) => (
                            <div key={i} className="h-48 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 animate-pulse" />
                        ))}
                    </div>
                )}
                {!loading && hasConnections && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {connections!.map(c => {
                            const providerMeta =
                                allProviders.find(p => p.id === c.Provider) ||
                                allProviders.find(p => p.id.toLowerCase() === (c.Provider || '').toLowerCase()) ||
                                allProviders.find(p => p.title.toLowerCase() === (c.Provider || '').toLowerCase()) ||
                                undefined
                            const highlight = justCreatedId && (c.Id === justCreatedId)
                            return <ConnectionCard key={c.Id || c.Name} connection={c} providerMeta={providerMeta} highlight={!!highlight} onManage={() => setManageConnectionId(c.Id || null)} />
                        })}
                    </div>
                )}
            </section>

            {/* Available Providers */}
            <section className="space-y-4">
                <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Available</h3>
                {loading && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 opacity-60 pointer-events-none select-none">
                        {Array.from({ length: 3 }).map((_, i) => (
                            <div key={i} className="h-48 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 animate-pulse" />
                        ))}
                    </div>
                )}
                {!loading && availableProviders.length === 0 && (
                    <p className="text-sm text-slate-500 dark:text-slate-400">All listed providers are connected.</p>
                )}
                {!loading && availableProviders.length > 0 && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {availableProviders.map(({ meta, blockedReason }) => (
                            <ProviderCard
                                key={meta.id}
                                provider={meta}
                                selected={selectedProvider === meta.id}
                                blockedReason={blockedReason}
                                onSelect={() => setSelectedProvider(meta.id)}
                            />
                        ))}
                    </div>
                )}
            </section>

            {/* Integration Panel (non-HubSpot providers) */}
            <IntegrationPanel
                    isOpen={panelOpen}
                    clientCompanyId={clientCompanyId}
                    selectedProvider={activeProviderMeta}
                    managingConnection={managingConnection || null}
                    allProviders={allProviders}
                    onClose={handleClosePanel}
                    onConnectionCreated={handleConnectionCreated}
                    onConnectionUpdated={handleConnectionUpdated}
                    onConnectionDeleted={handleConnectionDeleted}
                    onConnectionArchived={handleConnectionArchived}
                />

            {/* HubSpot Setup Wizard */}
            <HubSpotSetupWizard
                isOpen={hubSpotWizardOpen}
                blockedByCrm={connectedCrm}
                clientCompanyId={clientCompanyId}
                onClose={handleClosePanel}
                onComplete={() => { handleClosePanel(); load() }}
            />

            {/* Salesforce Connect Panel */}
            <SalesforceConnectPanel
                isOpen={salesforceConnectOpen}
                blockedByCrm={connectedCrm}
                clientCompanyId={clientCompanyId}
                onClose={() => { handleClosePanel(); load() }}
                onComplete={() => { handleClosePanel(); load() }}
            />

            {/* Dynamics 365 Connect Panel */}
            <DynamicsConnectPanel
                isOpen={dynamicsConnectOpen}
                blockedByCrm={connectedCrm}
                clientCompanyId={clientCompanyId}
                onClose={() => { handleClosePanel(); load() }}
                onComplete={() => { handleClosePanel(); load() }}
            />
        </div>
    )
}
